// Loads a pre-parsed project by id from the backend's /parse-script/imported
// store, drops it into arc state, and forwards to character pick.
//
// Use case: when /parse-script can't handle a file (huge scans that OOM the
// synchronous parse), the team runs an out-of-band per-page parse loop +
// aggregator + POST /parse-script/import, gets back a project_id, and hands
// the actor a URL like /import?id=<project_id>. This screen is the bridge.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getImportedProject } from "../api/client";
import { useArc } from "../state/arc";

export default function ImportScreen() {
  const navigate = useNavigate();
  const { update } = useArc();
  const [params] = useSearchParams();
  const id = params.get("id");
  const character = params.get("character");
  const [error, setError] = useState<string | null>(null);

  // Methodology stays null unless the link explicitly sets `?methodology=`.
  // Per Ryan 2026-06-25: don't auto-default Chubbuck — phantom journal
  // markers appear on /script with no actual prompts behind them.
  const methodology = params.get("methodology") || null;

  useEffect(() => {
    if (!id) {
      setError("No project id in the URL. Expected /import?id=<project_id>.");
      return;
    }
    let cancelled = false;
    getImportedProject(id)
      .then((envelope: any) => {
        if (cancelled) return;
        update({
          parsedProject: envelope.project,
          parsedScript: envelope.parsed_script ?? null,
          projectId: envelope.project_id ?? id,
          methodologyId: methodology,
          selectedCharacter: character || null,
        });
        // Persist to the local shelf so this project shows in Library next time.
        import("./Library").then(({ saveShelfEntry }) => {
          saveShelfEntry({
            projectId: envelope.project_id ?? id,
            scriptName: envelope.project?.script_name ?? "Untitled",
            character: character || null,
            lastVisited: Date.now(),
          });
        });
        // Always land in the Library so the recipient sees their script on
        // the shelf and chooses to enter the Rehearsal Room — per Ryan
        // 2026-06-25, never drop straight into the booth.
        navigate("/library", { replace: true });
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [id, methodology, navigate, update]);

  return (
    <section>
      <h2>Loading your script…</h2>
      {error ? (
        <>
          <p className="muted" style={{ color: "#e08383" }}>
            {error}
          </p>
          <button onClick={() => navigate("/upload")}>
            ← Upload a different script
          </button>
        </>
      ) : (
        <p className="muted">Fetching project {id}…</p>
      )}
    </section>
  );
}
